import { useCallback, useState } from 'react';
import { useAuth } from './useAuth';
import { InvitationService, UserRole } from '../services/invitationService';
import { UserOrganizationService, UserData } from '../services/userOrganizationService';

interface RemoveUserResult {
  success: boolean;
  error?: string;
}

interface ChangeRoleResult {
  success: boolean;
  error?: string;
}

interface ListUsersResult {
  data: UserData[];
  count: number;
}

interface UseUsersReturn {
  loading: boolean;
  error: string | null;
  users: UserData[] | null;
  userLoaded: boolean;
  inviteUser: (email: string) => Promise<boolean | undefined>;
  listUsers: (page?: number, itemsPerPage?: number) => Promise<ListUsersResult>;
  removeUser: (userId: string) => Promise<RemoveUserResult>;
  changeUserRole: (userId: string, newRole: UserRole) => Promise<ChangeRoleResult>;
}

const useUsers = (): UseUsersReturn => {
  const { userLoaded, currentOrganization } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserData[] | null>(null);

  const inviteUser = useCallback(
    async (email: string): Promise<boolean | undefined> => {
      if (!currentOrganization) {
        setError('No organization selected');
        return false;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await InvitationService.inviteUser({
          email,
          organizationId: currentOrganization.id,
          role: 'member'
        });

        if (!result.success) {
          throw new Error(result.message);
        }

        return true;
      } catch (error) {
        setError((error as Error).message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [currentOrganization]
  );

  const listUsers = useCallback(
    async (page = 1, itemsPerPage = 10): Promise<ListUsersResult> => {
      if (!currentOrganization) {
        setError('No organization selected');
        return { data: [], count: 0 };
      }

      setLoading(true);
      setError(null);
      try {
        const result = await UserOrganizationService.listUsers({
          organizationId: currentOrganization.id,
          page,
          itemsPerPage
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        setUsers(result.data);
        return { data: result.data, count: result.count };
      } catch (error) {
        setError((error as Error).message);
        return { data: [], count: 0 };
      } finally {
        setLoading(false);
      }
    },
    [currentOrganization]
  );

  const removeUser = useCallback(
    async (userId: string): Promise<RemoveUserResult> => {
      if (!currentOrganization) {
        return { success: false, error: 'No organization selected' };
      }

      setLoading(true);
      setError(null);
      try {
        const result = await UserOrganizationService.removeUser({
          userId,
          organizationId: currentOrganization.id
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        await listUsers();
        return { success: true };
      } catch (error) {
        const errorMessage = (error as Error).message;
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [currentOrganization, listUsers]
  );

  const changeUserRole = useCallback(
    async (userId: string, newRole: UserRole): Promise<ChangeRoleResult> => {
      if (!currentOrganization) {
        return { success: false, error: 'No organization selected' };
      }

      setLoading(true);
      setError(null);
      try {
        const result = await UserOrganizationService.changeUserRole({
          userId,
          organizationId: currentOrganization.id,
          newRole
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        await listUsers();
        return { success: true };
      } catch (error) {
        const errorMessage = (error as Error).message;
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [currentOrganization, listUsers]
  );

  return {
    loading,
    error,
    users,
    userLoaded,
    inviteUser,
    listUsers,
    removeUser,
    changeUserRole
  };
};

export default useUsers;
